/**
 * App layout: left vertical sidebar nav + top user bar + content area.
 *
 * Responsive behavior:
 *   - Desktop (≥ md breakpoint, ≥ 768px): permanent left sidebar with
 *     text labels + full top bar (presence badge, role, username, logout).
 *   - Mobile (< md): sidebar is hidden behind a hamburger button. Tapping
 *     it opens a left-edge drawer with the same nav, overlaying the
 *     content with a semi-transparent backdrop. The top bar shrinks to
 *     hamburger + brand + presence + role + logout (username hidden to
 *     free up horizontal space).
 */
import { useEffect, useState } from 'react'
import { LogOut, Menu, Monitor, ScrollText, Stethoscope, Users, X } from 'lucide-react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { cn } from '@/lib/cn'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { PresenceBadge } from '@/components/presence-badge'
import { useAuth } from '@/providers/auth-provider'

const tabs = [
  { to: '/dashboard', label: 'Giám sát', icon: Monitor },
  { to: '/employees', label: 'Nhân viên', icon: Users },
  { to: '/diagnostics', label: 'Chẩn đoán', icon: Stethoscope },
  { to: '/logs', label: 'Log', icon: ScrollText },
] as const

interface NavListProps {
  onNavigate?: () => void
}

/** Shared nav items — used by both the desktop sidebar and the mobile drawer. */
function NavList({ onNavigate }: NavListProps) {
  return (
    <>
      {tabs.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              isActive
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
            )
          }
        >
          <tab.icon className="h-4 w-4 shrink-0" />
          {tab.label}
        </NavLink>
      ))}
    </>
  )
}

export function Layout() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Close drawer on Escape so mobile users have a keyboard escape hatch
  // (also closes when the user navigates via NavList's onClick).
  useEffect(() => {
    if (!drawerOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [drawerOpen])

  // Lock body scroll while drawer is open so the page behind doesn't
  // scroll when the user swipes over the backdrop on iOS Safari.
  useEffect(() => {
    if (!drawerOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [drawerOpen])

  async function handleLogout() {
    setDrawerOpen(false)
    await logout()
    navigate('/login', { replace: true })
  }

  return (
    <div className="flex h-full bg-slate-50 dark:bg-slate-950">
      {/* Desktop sidebar (≥ md). On mobile it's hidden — the drawer below
          plays the same role. */}
      <aside className="hidden w-56 flex-col border-r bg-background md:flex">
        {/* Brand */}
        <div className="flex h-14 items-center gap-2 border-b px-4">
          <img
            src="/logo.svg"
            alt="Company Monitor"
            width={32}
            height={32}
            // On the (light) sidebar background, multiply blend makes the
            // white parts of the source logo transparent so the green/black
            // elements stand out without a white halo.
            className="h-8 w-8 rounded-md mix-blend-multiply"
          />
          <span className="font-semibold">Company Monitor</span>
        </div>

        {/* Nav — grows to fill, leaves room below for future groups */}
        <nav className="flex flex-1 flex-col gap-1 p-2">
          <NavList />
        </nav>
      </aside>

      {/* Mobile drawer (< md). Hidden on desktop. Slides in from the left
          when the hamburger button is tapped. */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 md:hidden" role="dialog" aria-modal="true">
          {/* Backdrop — clicking it closes the drawer. */}
          <button
            type="button"
            aria-label="Đóng menu"
            className="absolute inset-0 bg-black/40"
            onClick={() => setDrawerOpen(false)}
          />
          {/* Drawer panel */}
          <aside className="absolute inset-y-0 left-0 flex w-64 max-w-[85vw] flex-col bg-background shadow-xl">
            <div className="flex h-14 items-center justify-between border-b px-4">
              <div className="flex items-center gap-2">
                <img
                  src="/logo.svg"
                  alt="Company Monitor"
                  width={32}
                  height={32}
                  className="h-8 w-8 rounded-md mix-blend-multiply"
                />
                <span className="font-semibold">Company Monitor</span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setDrawerOpen(false)}
                aria-label="Đóng menu"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <nav className="flex flex-1 flex-col gap-1 p-2">
              <NavList onNavigate={() => setDrawerOpen(false)} />
            </nav>
          </aside>
        </div>
      )}

      {/* Main column: top bar + content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-14 items-center justify-between gap-2 border-b bg-background px-3 md:px-4">
          {/* Mobile only — hamburger to open the drawer. */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setDrawerOpen(true)}
            aria-label="Mở menu"
          >
            <Menu className="h-5 w-5" />
          </Button>

          {/* Brand on mobile (desktop shows it in the sidebar instead). */}
          <div className="flex items-center gap-2 md:hidden">
            <img
              src="/logo.svg"
              alt="Company Monitor"
              width={28}
              height={28}
              className="h-7 w-7 rounded-md mix-blend-multiply"
            />
            <span className="truncate text-sm font-semibold">Company Monitor</span>
          </div>

          {/* Page-title slot for desktop (left-aligned next to no hamburger). */}
          <div className="hidden text-sm text-muted-foreground md:block">
            {/* breadcrumb / page title slot — populated per-page later */}
          </div>

          {/* Right cluster: presence, role, username (desktop only), logout. */}
          <div className="flex items-center gap-2 md:gap-3">
            <PresenceBadge />
            <Badge variant="secondary">{user?.role}</Badge>
            <span className="hidden text-sm text-muted-foreground md:inline">
              {user?.username}
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              aria-label="Đăng xuất"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </header>
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  )
}